import { useApi } from '@/composables/useApi'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'
import type { Message, MessageThread } from '@farmfree/common/types/message/message'
import { getFirebaseDb } from '@/services/firebaseService'
import { decryptWithIv } from '@farmfree/common/utils/crypto/decryption'
import { collection, getDocs, Timestamp } from 'firebase/firestore'
import { logger } from '@/utils/logger'
import { getEncryptionKey } from '@/env'

/** Firestore 메시지 문서의 원본 데이터 타입 */
interface FirestoreMessageData {
  id?: string
  address: string
  messageBody: string
  dateMessageCreated: Timestamp
  isSent: boolean
  type: 'sms' | 'mms' | 'rcs'
  thread_id?: string
}

/** 메시지를 포함한 Firestore 문서 타입 */
interface FirestoreMessageDoc {
  iv?: string
  messages: FirestoreMessageData[]
}

export function createMessageService() {
  const api = useApi()

  /** 메시지 파싱 함수 - Firestore 원본 데이터를 Message 타입으로 변환 */
  const parseMessage = (
    message: FirestoreMessageData,
    docId: string,
    documentIv: string = ''
  ): Message => {
    // IV가 있으면 복호화 적용
    const decryptedAddress: string =
      documentIv && message.address
        ? decryptWithIv(message.address, documentIv, getEncryptionKey())
        : message.address

    const decryptedMessageBody: string =
      documentIv && message.messageBody
        ? decryptWithIv(message.messageBody, documentIv, getEncryptionKey())
        : message.messageBody

    return {
      id: message.id || `${docId}_${Date.now()}_${Math.random()}`,
      address: decryptedAddress,
      messageBody: decryptedMessageBody,
      dateMessageCreated: message.dateMessageCreated || Timestamp.fromDate(new Date()),
      isSent: typeof message.isSent === 'boolean' ? message.isSent : false,
      type: message.type || 'sms',
      thread_id: message.thread_id || message.address,
    }
  }

  return {
    /**
     * SMS 메시지 전체 조회
     *
     * @param adminUid - 관리자 UID
     * @param since - 타임스탬프 (번들 문서 구조로 인해 클라이언트 측 필터링에만 사용)
     */
    async getAllSmsMessagesWithCache(
      adminUid: string,
      since: number = 0
    ): Promise<ApiResponse<Message[]>> {
      try {
        logger.message.info('메시지 로딩 시작', { adminUid, since })

        const db = getFirebaseDb()
        const smsMessagesRef = collection(db, 'v2_administrators', adminUid, 'sms_backup')

        logger.message.debug('Firestore 조회: 모든 문서')

        const querySnapshot = await getDocs(smsMessagesRef)

        const messages: Message[] = []
        let docsRead = 0

        querySnapshot.forEach((docSnapshot) => {
          docsRead++
          const data: FirestoreMessageDoc = docSnapshot.data() as FirestoreMessageDoc
          const documentIv: string = data.iv || ''

          if (data.messages && Array.isArray(data.messages)) {
            data.messages.forEach((message: FirestoreMessageData) => {
              messages.push(parseMessage(message, docSnapshot.id, documentIv))
            })
          }
        })

        messages.sort((a: Message, b: Message) => {
          const timeA: number =
            typeof a.dateMessageCreated === 'number'
              ? a.dateMessageCreated
              : a.dateMessageCreated?.toMillis() || 0
          const timeB: number =
            typeof b.dateMessageCreated === 'number'
              ? b.dateMessageCreated
              : b.dateMessageCreated?.toMillis() || 0
          return timeB - timeA
        })

        logger.message.info('메시지 로딩 완료', {
          총메시지수: messages.length,
          Firestore읽기: `${docsRead}개 문서`,
        })

        return {
          isSuccess: true,
          data: messages,
          message: `SMS 메시지 데이터를 성공적으로 가져왔습니다. (${docsRead}개 문서 읽기)`,
        }
      } catch (error: unknown) {
        const errorData = error instanceof Error ? error : { error: String(error) }
        logger.message.error('메시지 로딩 실패', errorData)

        const errorMessage: string =
          error instanceof Error ? error.message : 'SMS 메시지 데이터를 가져오는데 실패했습니다.'

        return {
          isSuccess: false,
          data: [],
          message: errorMessage,
        }
      }
    },
  }
}
